// Legacy admin pages no longer load TinyMCE with a render-blocking <script> in <head>. The layout
// names the build in <meta name="scout-tinymce">, and this loader injects it the first time a page
// actually needs an editor. Pages that still load TinyMCE eagerly have no meta, so the global wins.

import { guardTinyMCE4CaretFromPoint } from './tinymce4CaretFix';

interface TinyMCEGlobal {
  majorVersion: string;
  init: (settings: Record<string, unknown>) => unknown;
  get: (id: string) => unknown;
}

declare global {
  interface Window {
    tinyMCE?: TinyMCEGlobal;
  }
}

const META_SELECTOR = 'meta[name="scout-tinymce"]';

let pending: Promise<TinyMCEGlobal | undefined> | null = null;

const lazyMeta = (): HTMLMetaElement | null => document.querySelector(META_SELECTOR);

export const tinyMCEIsLoaded = (): boolean => typeof window.tinyMCE !== 'undefined';

// True on a page whose TinyMCE is loaded on demand and has not been loaded yet.
export const tinyMCENeedsLoading = (): boolean => !tinyMCEIsLoaded() && lazyMeta() !== null;

// The major version the page uses, known before the script itself has loaded.
export const tinyMCEMajorVersion = (): string | undefined =>
  window.tinyMCE?.majorVersion ?? lazyMeta()?.dataset.majorVersion;

// Every editor init on legacy pages goes through here, so TinyMCE 4 picks up its caret fix once.
const guarded = (tinymce: TinyMCEGlobal | undefined): TinyMCEGlobal | undefined => {
  guardTinyMCE4CaretFromPoint(tinymce);
  return tinymce;
};

// A TinyMCE loaded by an eager <script> is already global when the packs evaluate, before any
// DOM-ready init, so editors that never call loadTinyMCE still get the fix.
guarded(window.tinyMCE);

export const loadTinyMCE = async (): Promise<TinyMCEGlobal | undefined> => {
  if (tinyMCEIsLoaded()) return guarded(window.tinyMCE);

  const src = lazyMeta()?.content;
  if (!src) return undefined;

  pending ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve(guarded(window.tinyMCE));
    script.onerror = () => {
      pending = null;
      script.remove();
      reject(new Error(`TinyMCE failed to load from ${src}`));
    };
    document.head.appendChild(script);
  });

  return pending;
};

// For the jQuery editors: load TinyMCE only when the page has a matching textarea, then init.
export const initTinyMCEWhenPresent = async (
  selector: string,
  settings: Record<string, unknown>
): Promise<unknown> => {
  if (!document.querySelector(selector)) return undefined;

  const tinymce = await loadTinyMCE();
  return tinymce?.init({ selector, ...settings });
};

export const resetTinyMCELoaderForTests = (): void => {
  pending = null;
};
